"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { 
  FaArrowRight, 
  FaRegClock, 
  FaUserGraduate, 
  FaDownload,
  FaStar
} from "react-icons/fa";
import { LuIndianRupee } from "react-icons/lu";

const CourseCard = ({
  thumbnail,
  title,
  duration,
  discountedPrice,
  price,
  brochureUrl,
  slug,
  index = 0 // Default to 0 if not provided
}) => {
  const cardRef = useRef(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    
    // Simple fade up animation
    gsap.fromTo(
      cardRef.current,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        delay: index * 0.1, // Stagger effect based on index
        ease: "power2.out",
        scrollTrigger: {
          trigger: cardRef.current,
          start: "top 90%",
        },
      }
    );
  }, [index]);

  // Calculate discount percentage
  const discountPercent = Math.round(((price - discountedPrice) / price) * 100);

  return (
    <div 
      ref={cardRef}
      className="group relative flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200 hover:shadow-xl hover:border-blue-200 transition-all duration-300 h-full transform hover:-translate-y-1"
    >
      {/* Image Container */}
      <div className="relative w-full h-56 overflow-hidden">
        <Image
          src={thumbnail}
          alt={title}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {/* Floating Badge */}
        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-blue-700 shadow-sm flex items-center gap-1">
          <FaStar className="text-yellow-400" /> Premium
        </div>
        
        {/* Discount Badge */}
        {discountPercent > 0 && (
          <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-sm">
            {discountPercent}% OFF
          </div>
        )}
      </div>

      {/* Content Container */}
      <div className="flex flex-col flex-grow p-6">
        
        {/* Meta Data */}
        <div className="flex items-center gap-4 text-slate-500 text-xs font-medium mb-3">
          <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md">
            <FaRegClock className="text-blue-500" />
            <span>{duration}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md">
            <FaUserGraduate className="text-purple-500" />
            <span>Job Ready</span>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-slate-900 mb-3 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
          {title}
        </h3>

        {/* Price Section */}
        <div className="mt-auto pt-4 border-t border-slate-100 flex items-end justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-slate-400 font-medium mb-0.5">Course Fee</span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-slate-900 flex items-center">
                <LuIndianRupee className="w-5 h-5" />
                {discountedPrice.toLocaleString('en-IN')}
              </span>
              {price > discountedPrice && (
                <span className="text-sm text-slate-400 line-through flex items-center">
                  <LuIndianRupee className="w-3 h-3" />
                  {price.toLocaleString('en-IN')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3 mt-6">
          {brochureUrl ? (
             <Link 
             href={brochureUrl}
             target="_blank"
             className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 hover:text-slate-900 transition-colors"
           >
             <FaDownload size={14} />
             <span>Syllabus</span>
           </Link>
          ) : (
             <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-100 text-slate-300 text-sm font-semibold cursor-not-allowed">
              <FaDownload size={14} />
              <span>Syllabus</span>
             </div>
          )}
         
          <Link 
            href={`/premium-courses/${slug}`}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-semibold shadow-md shadow-blue-200 hover:shadow-lg hover:shadow-blue-300 hover:from-blue-700 hover:to-blue-800 transition-all transform active:scale-95"
          >
            <span>Enroll Now</span>
            <FaArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CourseCard;